/**
 * Cliente del API de novedades del portal (goal novedades-del-portal 1, módulo
 * `novedades/` de tecopos-portal-server). Las dos rutas van con Bearer de
 * Identidad —«qué le toca a esta persona y qué ya vio» no existe sin persona—,
 * así que solo se llaman desde el área autenticada.
 *
 *   GET  /novedades/al-entrar → { pendiente, novedades[] }
 *   POST /novedades/visto     → mueve la marca de agua a «ahora»
 */
import { getPortalApiBase } from '@/lib/env'
import { tokenStore } from '@/lib/auth/tokenStore'

const ENDPOINT = `${getPortalApiBase()}/novedades`

/** Lo que se le cuenta al usuario de un producto con actualización. */
export interface NovedadDeProducto {
  /** Slug del repo origen (ej. "tecopos-admin"): lo que entiende `?repo=`. */
  repo: string
  /** Rótulo del producto en lenguaje de cliente (texto libre del pipeline). */
  product: string
  version: string
  /** ISO del corte a producción. */
  releasedAt: string
  title: string
  /** El destacado de la nota. El servidor nunca lo manda vacío aquí. */
  resumen: string
}

export interface NovedadesAlEntrar {
  /** Hay algo que esta persona todavía no ha visto. */
  pendiente: boolean
  novedades: NovedadDeProducto[]
}

/**
 * Fallo del API de novedades, con el código HTTP a mano. Existe para poder
 * distinguir el 404 ESPERADO —el servidor de este entorno todavía no tiene el
 * módulo del goal 1; la web se sube después, lockstep raíz §6.1— de un fallo
 * que sí es una incidencia. Sin el código, o se reporta todo (ruido durante el
 * despliegue) o no se reporta nada (la pantalla desaparece en silencio).
 */
export class ErrorDeNovedades extends Error {
  // Campo declarado a mano y no como propiedad de parámetro: el tsconfig del
  // repo corre con `erasableSyntaxOnly`, que prohíbe la forma corta.
  readonly status: number

  constructor(status: number, mensaje: string) {
    super(mensaje)
    this.name = 'ErrorDeNovedades'
    this.status = status
  }
}

/**
 * Un fallo que merece llegar a Sentry. Se callan dos:
 * - **404 / 501**: servidor sin el módulo. Es el estado normal entre que se
 *   despliega la web y se despliega el servidor.
 * - **Fallo de red** (la promesa de `fetch` se rompe, sin respuesta): en Cuba
 *   es la norma, no el borde (raíz §6.7).
 */
export function esFalloReportable(error: unknown): boolean {
  if (!(error instanceof ErrorDeNovedades)) return false
  return error.status !== 404 && error.status !== 501
}

function authHeaders(): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) throw new ErrorDeNovedades(res.status, `${ctx}: ${res.status}`)
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

/**
 * Lo que alimenta el aviso al entrar y la página propia. **Lanza** si el
 * servidor no responde o responde mal (404 de un servidor viejo incluido): la
 * política de qué hacer con ese fallo la decide cada pantalla, no el cliente.
 *
 * Lo que sí normaliza es la FORMA: un cuerpo vacío o sin `novedades` sale como
 * `{ pendiente: false, novedades: [] }`, para que nadie tenga que defenderse de
 * un `undefined.map`.
 */
export async function fetchNovedadesAlEntrar(): Promise<NovedadesAlEntrar> {
  const res = await fetch(`${ENDPOINT}/al-entrar`, { headers: authHeaders() })
  const data = await json<Partial<NovedadesAlEntrar> | null>(
    res,
    'GET novedades/al-entrar',
  )
  return {
    pendiente: data?.pendiente === true,
    novedades: Array.isArray(data?.novedades) ? data.novedades : [],
  }
}

/**
 * Marca de agua: «ya lo vio». Idempotente en el servidor (upsert por `sub`),
 * así que reintentarlo ni rompe ni duplica.
 *
 * 🚨 Es efecto del CIERRE, nunca del render: marcarlo al abrir quemaría la
 * novedad de quien cerró la pestaña sin leerla.
 */
export async function marcarNovedadesVistas(): Promise<void> {
  const res = await fetch(`${ENDPOINT}/visto`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) {
    throw new ErrorDeNovedades(
      res.status,
      `POST novedades/visto: ${res.status}`,
    )
  }
}

/**
 * Fecha del corte, corta y sin hora («28 ago 2026»). La tarjeta del histórico
 * usa `formatReleaseDate` —larga y con hora— porque ahí la nota es la página;
 * aquí compite con el título dentro de una fila de dos líneas.
 */
export function formatFechaCorta(iso: string, locale = 'es'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
